/**
 * Exercise Library
 *
 * NASM OPT-aligned movement bank used by movementEngine.ts to generate
 * personalized sessions. Each entry carries:
 *
 *  - tier: compound | accessory | core | mobility
 *  - pattern: squat / hinge / push / pull / lunge / carry / core / mobility / cardio
 *  - contraindications: InjuryFlag[] — engine drops any exercise overlapping
 *    with an active (non-paused) injury flag
 *  - goalEmphasis: which fitness goals this exercise serves best
 *  - GGS Pre/Postnatal V2 status for pregnant + postpartum routing
 *
 * Coverage targets:
 *  - At least 2 accessory options per pattern so block A vs block B differ
 *  - At least one compound + one accessory per major pattern
 *  - Pelvic-floor and core-restore primitives for postpartum return
 *  - Joint-friendly substitutes for women with knee, hip, or shoulder issues
 *
 * Sources cited in the cue text where appropriate:
 *  - NASM OPT model phases 1-3 (stabilization, strength, power)
 *  - GGS Pregnancy and Postpartum Athleticism V2 (Brianna Battles)
 */

import type { ExerciseDef } from "./store";

export const EXERCISE_LIBRARY: ExerciseDef[] = [
  /* ============================ COMPOUNDS ============================ */
  // Squat
  {
    id: "ex_goblet_squat",
    name: "Goblet Squat",
    tier: "compound",
    pattern: "squat",
    equipment: "dumbbell",
    cue: "Hold the dumbbell at your chest, feet shoulder width, sit between your hips.",
    contraindications: [],
    goalEmphasis: ["build-strength", "lose-weight"],
    ggsPregnant: "safe",
    ggsPostpartum: "safe",
  },
  {
    id: "ex_back_squat",
    name: "Back Squat",
    tier: "compound",
    pattern: "squat",
    equipment: "barbell",
    cue: "Bar on the upper traps, brace, knees track over toes.",
    contraindications: ["spine"],
    goalEmphasis: ["build-strength"],
    ggsPregnant: "modify",
    ggsPostpartum: "safe",
  },
  {
    id: "ex_box_squat",
    name: "Box Squat",
    tier: "compound",
    pattern: "squat",
    equipment: "dumbbell",
    cue: "Tap a low box behind you, stand with control. Joint-friendly depth limiter.",
    contraindications: [],
    goalEmphasis: ["build-strength", "postpartum-recovery"],
    ggsPregnant: "safe",
    ggsPostpartum: "safe",
  },
  {
    id: "ex_wall_sit",
    name: "Wall Sit",
    tier: "compound",
    pattern: "squat",
    equipment: "bodyweight",
    cue: "Back flat on wall, knees at 90, breathe through the burn.",
    contraindications: [],
    goalEmphasis: ["build-strength", "postpartum-recovery"],
    ggsPregnant: "safe",
    ggsPostpartum: "safe",
  },

  // Hinge
  {
    id: "ex_dumbbell_rdl",
    name: "Dumbbell Romanian Deadlift",
    tier: "compound",
    pattern: "hinge",
    equipment: "dumbbell",
    cue: "Hinge at the hips, soft knees, dumbbells stay close to legs.",
    contraindications: [],
    goalEmphasis: ["build-strength", "postpartum-recovery"],
    ggsPregnant: "safe",
    ggsPostpartum: "safe",
  },
  {
    id: "ex_kettlebell_deadlift",
    name: "Kettlebell Deadlift",
    tier: "compound",
    pattern: "hinge",
    equipment: "kettlebell",
    cue: "Bell between feet, push the floor away, stand tall.",
    contraindications: [],
    goalEmphasis: ["build-strength", "lose-weight"],
    ggsPregnant: "safe",
    ggsPostpartum: "safe",
  },
  {
    id: "ex_hip_thrust",
    name: "Hip Thrust",
    tier: "compound",
    pattern: "hinge",
    equipment: "barbell",
    cue: "Shoulders on bench, drive through heels, ribs down at the top.",
    contraindications: [],
    goalEmphasis: ["build-strength"],
    ggsPregnant: "modify",
    ggsPostpartum: "safe",
  },
  {
    id: "ex_glute_bridge",
    name: "Glute Bridge",
    tier: "compound",
    pattern: "hinge",
    equipment: "bodyweight",
    cue: "Exhale, tuck the tailbone, lift through the glutes not the back.",
    contraindications: [],
    goalEmphasis: ["postpartum-recovery", "flexibility"],
    ggsPregnant: "safe",
    ggsPostpartum: "core-restore",
  },

  // Push horizontal
  {
    id: "ex_pushup",
    name: "Push-up",
    tier: "compound",
    pattern: "push-horizontal",
    equipment: "bodyweight",
    cue: "Hands under shoulders, ribs over hips, exhale on the press.",
    contraindications: [],
    goalEmphasis: ["build-strength"],
    ggsPregnant: "modify",
    ggsPostpartum: "safe",
  },
  {
    id: "ex_dumbbell_bench",
    name: "Dumbbell Bench Press",
    tier: "compound",
    pattern: "push-horizontal",
    equipment: "dumbbell",
    cue: "Wrists stacked over elbows, press to lockout without flaring.",
    contraindications: [],
    goalEmphasis: ["build-strength"],
    ggsPregnant: "modify",
    ggsPostpartum: "safe",
  },
  {
    id: "ex_incline_pushup",
    name: "Incline Push-up",
    tier: "accessory",
    pattern: "push-horizontal",
    equipment: "bodyweight",
    cue: "Hands on bench, body straight line, breathe on the way down.",
    contraindications: [],
    goalEmphasis: ["postpartum-recovery", "build-strength"],
    ggsPregnant: "safe",
    ggsPostpartum: "safe",
  },
  {
    id: "ex_floor_press",
    name: "Dumbbell Floor Press",
    tier: "accessory",
    pattern: "push-horizontal",
    equipment: "dumbbell",
    cue: "Elbows touch floor, press straight up, shoulder-friendly.",
    contraindications: [],
    goalEmphasis: ["build-strength", "postpartum-recovery"],
    ggsPregnant: "safe",
    ggsPostpartum: "safe",
  },

  // Push vertical
  {
    id: "ex_dumbbell_press",
    name: "Dumbbell Shoulder Press",
    tier: "compound",
    pattern: "push-vertical",
    equipment: "dumbbell",
    cue: "Ribs stacked over hips, press without flaring the rib cage.",
    contraindications: [],
    goalEmphasis: ["build-strength"],
    ggsPregnant: "modify",
    ggsPostpartum: "safe",
  },
  {
    id: "ex_landmine_press",
    name: "Landmine Press",
    tier: "accessory",
    pattern: "push-vertical",
    equipment: "barbell",
    cue: "Press at an angle, ribs down, joint-friendly arc.",
    contraindications: [],
    goalEmphasis: ["build-strength", "postpartum-recovery"],
    ggsPregnant: "safe",
    ggsPostpartum: "safe",
  },
  {
    id: "ex_band_overhead_press",
    name: "Band Overhead Press",
    tier: "accessory",
    pattern: "push-vertical",
    equipment: "band",
    cue: "Stand on band, press straight up, shoulders away from ears.",
    contraindications: [],
    goalEmphasis: ["build-strength", "flexibility"],
    ggsPregnant: "safe",
    ggsPostpartum: "safe",
  },

  // Pull horizontal
  {
    id: "ex_dumbbell_row",
    name: "Single-Arm Dumbbell Row",
    tier: "compound",
    pattern: "pull-horizontal",
    equipment: "dumbbell",
    cue: "Hand and knee on bench, pull elbow past ribs, square hips.",
    contraindications: [],
    goalEmphasis: ["build-strength", "postpartum-recovery"],
    ggsPregnant: "safe",
    ggsPostpartum: "safe",
  },
  {
    id: "ex_inverted_row",
    name: "Inverted Row",
    tier: "compound",
    pattern: "pull-horizontal",
    equipment: "bodyweight",
    cue: "Hang under bar, pull chest to bar, body in a plank.",
    contraindications: [],
    goalEmphasis: ["build-strength"],
    ggsPregnant: "modify",
    ggsPostpartum: "safe",
  },
  {
    id: "ex_seated_row_band",
    name: "Seated Band Row",
    tier: "accessory",
    pattern: "pull-horizontal",
    equipment: "band",
    cue: "Sit tall, pull band to ribs, shoulder blades down and back.",
    contraindications: [],
    goalEmphasis: ["postpartum-recovery", "flexibility"],
    ggsPregnant: "safe",
    ggsPostpartum: "safe",
  },
  {
    id: "ex_chest_supported_row",
    name: "Chest Supported Row",
    tier: "accessory",
    pattern: "pull-horizontal",
    equipment: "dumbbell",
    cue: "Chest on incline bench, pull elbows back, no momentum.",
    contraindications: [],
    goalEmphasis: ["build-strength", "postpartum-recovery"],
    ggsPregnant: "safe",
    ggsPostpartum: "safe",
  },

  // Pull vertical
  {
    id: "ex_lat_pulldown_band",
    name: "Band Lat Pulldown",
    tier: "compound",
    pattern: "pull-vertical",
    equipment: "band",
    cue: "Anchor band overhead, pull to collarbones, ribs stay down.",
    contraindications: [],
    goalEmphasis: ["build-strength", "postpartum-recovery"],
    ggsPregnant: "safe",
    ggsPostpartum: "safe",
  },
  {
    id: "ex_pullup_assisted",
    name: "Assisted Pull-up",
    tier: "compound",
    pattern: "pull-vertical",
    equipment: "machine",
    cue: "Drive elbows down, chin clears the bar, control descent.",
    contraindications: ["spine"],
    goalEmphasis: ["build-strength"],
    ggsPregnant: "modify",
    ggsPostpartum: "safe",
  },
  {
    id: "ex_straight_arm_pulldown",
    name: "Straight Arm Band Pulldown",
    tier: "accessory",
    pattern: "pull-vertical",
    equipment: "band",
    cue: "Soft elbows, pull band to thighs, lats engage.",
    contraindications: [],
    goalEmphasis: ["postpartum-recovery", "flexibility"],
    ggsPregnant: "safe",
    ggsPostpartum: "safe",
  },

  // Lunge
  {
    id: "ex_reverse_lunge",
    name: "Reverse Lunge",
    tier: "compound",
    pattern: "lunge",
    equipment: "dumbbell",
    cue: "Step back, knee toward floor, drive through front heel. Knee-friendly.",
    contraindications: [],
    goalEmphasis: ["build-strength", "lose-weight"],
    ggsPregnant: "safe",
    ggsPostpartum: "safe",
  },
  {
    id: "ex_split_squat",
    name: "Bulgarian Split Squat",
    tier: "compound",
    pattern: "lunge",
    equipment: "dumbbell",
    cue: "Back foot on bench, sink into front leg, square hips.",
    contraindications: ["joints"],
    goalEmphasis: ["build-strength"],
    ggsPregnant: "modify",
    ggsPostpartum: "safe",
  },
  {
    id: "ex_step_up",
    name: "Step-up",
    tier: "accessory",
    pattern: "lunge",
    equipment: "dumbbell",
    cue: "Whole foot on the box, drive up, lower with control.",
    contraindications: [],
    goalEmphasis: ["build-strength", "postpartum-recovery"],
    ggsPregnant: "safe",
    ggsPostpartum: "safe",
  },
  {
    id: "ex_curtsy_lunge",
    name: "Curtsy Lunge",
    tier: "accessory",
    pattern: "lunge",
    equipment: "bodyweight",
    cue: "Step one foot diagonally behind, sink, drive up. Glute med focus.",
    contraindications: ["joints"],
    goalEmphasis: ["flexibility", "build-strength"],
    ggsPregnant: "safe",
    ggsPostpartum: "safe",
  },

  // Carry
  {
    id: "ex_farmer_carry",
    name: "Farmer Carry",
    tier: "compound",
    pattern: "carry",
    equipment: "dumbbell",
    cue: "Heavy in both hands, walk tall, ribs over hips, no leaning.",
    contraindications: [],
    goalEmphasis: ["build-strength", "lose-weight"],
    ggsPregnant: "modify",
    ggsPostpartum: "safe",
  },
  {
    id: "ex_suitcase_carry",
    name: "Suitcase Carry",
    tier: "accessory",
    pattern: "carry",
    equipment: "kettlebell",
    cue: "Weight in one hand, resist the lean, anti-lateral flexion.",
    contraindications: [],
    goalEmphasis: ["build-strength", "postpartum-recovery"],
    ggsPregnant: "safe",
    ggsPostpartum: "safe",
  },

  /* ============================== CORE =============================== */
  // Anti-extension
  {
    id: "ex_dead_bug",
    name: "Dead Bug",
    tier: "core",
    pattern: "core-anti-extension",
    equipment: "bodyweight",
    cue: "Low back stays glued to floor, exhale as opposite arm and leg reach.",
    contraindications: [],
    goalEmphasis: ["postpartum-recovery", "build-strength"],
    ggsPregnant: "safe",
    ggsPostpartum: "core-restore",
  },
  {
    id: "ex_plank",
    name: "Forearm Plank",
    tier: "core",
    pattern: "core-anti-extension",
    equipment: "bodyweight",
    cue: "Ribs over hips, glutes on, breathe. Watch for coning if pregnant.",
    contraindications: [],
    goalEmphasis: ["build-strength"],
    ggsPregnant: "modify",
    ggsPostpartum: "safe",
  },
  {
    id: "ex_hollow_hold",
    name: "Hollow Body Hold",
    tier: "core",
    pattern: "core-anti-extension",
    equipment: "bodyweight",
    cue: "Low back pressed into floor, arms and legs reach long.",
    contraindications: [],
    goalEmphasis: ["build-strength"],
    ggsPregnant: "avoid",
    ggsPostpartum: "modify",
  },

  // Anti-rotation
  {
    id: "ex_pallof_press",
    name: "Band Pallof Press",
    tier: "core",
    pattern: "core-anti-rotation",
    equipment: "band",
    cue: "Resist the band pulling you, press straight out, hold, return.",
    contraindications: [],
    goalEmphasis: ["build-strength", "postpartum-recovery"],
    ggsPregnant: "safe",
    ggsPostpartum: "safe",
  },
  {
    id: "ex_bird_dog",
    name: "Bird Dog",
    tier: "core",
    pattern: "core-anti-rotation",
    equipment: "bodyweight",
    cue: "Quadruped, opposite arm and leg reach, ribs stable, slow tempo.",
    contraindications: [],
    goalEmphasis: ["postpartum-recovery", "flexibility"],
    ggsPregnant: "safe",
    ggsPostpartum: "core-restore",
  },

  // Pelvic floor / core-restore
  {
    id: "ex_connection_breath",
    name: "Connection Breath",
    tier: "core",
    pattern: "core-pelvic-floor",
    equipment: "bodyweight",
    cue: "Inhale into 360 ribs, exhale to lift the pelvic floor. GGS V2 baseline.",
    contraindications: [],
    goalEmphasis: ["postpartum-recovery"],
    ggsPregnant: "safe",
    ggsPostpartum: "core-restore",
  },
  {
    id: "ex_heel_slides",
    name: "Heel Slides",
    tier: "core",
    pattern: "core-pelvic-floor",
    equipment: "bodyweight",
    cue: "On back, slide one heel out then in, exhale on effort, no doming.",
    contraindications: [],
    goalEmphasis: ["postpartum-recovery"],
    ggsPregnant: "safe",
    ggsPostpartum: "core-restore",
  },
  {
    id: "ex_kegel_progression",
    name: "Pelvic Floor Lift",
    tier: "core",
    pattern: "core-pelvic-floor",
    equipment: "bodyweight",
    cue: "Inhale to relax, exhale to lift, then fully release. Both halves matter.",
    contraindications: [],
    goalEmphasis: ["postpartum-recovery"],
    ggsPregnant: "safe",
    ggsPostpartum: "core-restore",
  },

  /* ============================ MOBILITY ============================= */
  // Hip
  {
    id: "ex_90_90_hip",
    name: "90/90 Hip Switch",
    tier: "mobility",
    pattern: "hip-mobility",
    equipment: "bodyweight",
    cue: "Sit between two right angles, rotate slowly between sides.",
    contraindications: [],
    goalEmphasis: ["flexibility", "postpartum-recovery"],
    ggsPregnant: "safe",
    ggsPostpartum: "safe",
  },
  {
    id: "ex_hip_flexor_stretch",
    name: "Half-Kneeling Hip Flexor Stretch",
    tier: "mobility",
    pattern: "hip-mobility",
    equipment: "bodyweight",
    cue: "Tuck the tailbone, squeeze the back glute, breathe.",
    contraindications: [],
    goalEmphasis: ["flexibility", "postpartum-recovery"],
    ggsPregnant: "safe",
    ggsPostpartum: "safe",
  },
  {
    id: "ex_world_greatest",
    name: "World's Greatest Stretch",
    tier: "mobility",
    pattern: "hip-mobility",
    equipment: "bodyweight",
    cue: "Lunge, hand inside foot, rotate up to ceiling, breathe at end range.",
    contraindications: [],
    goalEmphasis: ["flexibility"],
    ggsPregnant: "modify",
    ggsPostpartum: "safe",
  },

  // Thoracic
  {
    id: "ex_thoracic_rotation",
    name: "Quadruped T-Spine Rotation",
    tier: "mobility",
    pattern: "thoracic-mobility",
    equipment: "bodyweight",
    cue: "Hand behind head, rotate elbow to ceiling, breathe through it.",
    contraindications: [],
    goalEmphasis: ["flexibility", "postpartum-recovery"],
    ggsPregnant: "safe",
    ggsPostpartum: "safe",
  },
  {
    id: "ex_open_book",
    name: "Open Book",
    tier: "mobility",
    pattern: "thoracic-mobility",
    equipment: "bodyweight",
    cue: "Side-lying, open the top arm to the floor, eyes follow the hand.",
    contraindications: [],
    goalEmphasis: ["flexibility"],
    ggsPregnant: "safe",
    ggsPostpartum: "safe",
  },
  {
    id: "ex_cat_cow",
    name: "Cat-Cow",
    tier: "mobility",
    pattern: "thoracic-mobility",
    equipment: "bodyweight",
    cue: "Inhale to arch, exhale to round, slow tempo, no force.",
    contraindications: [],
    goalEmphasis: ["flexibility", "postpartum-recovery"],
    ggsPregnant: "safe",
    ggsPostpartum: "safe",
  },

  /* ============================= CARDIO ============================== */
  {
    id: "ex_incline_walk",
    name: "Incline Walk",
    tier: "mobility",
    pattern: "cardio",
    equipment: "machine",
    cue: "Treadmill at 10% incline, brisk pace, conversational breath.",
    contraindications: [],
    goalEmphasis: ["lose-weight", "postpartum-recovery"],
    ggsPregnant: "safe",
    ggsPostpartum: "safe",
  },
  {
    id: "ex_kettlebell_swing",
    name: "Kettlebell Swing",
    tier: "accessory",
    pattern: "cardio",
    equipment: "kettlebell",
    cue: "Hinge, snap the hips, bell floats to chest height. Power output.",
    contraindications: ["spine"],
    goalEmphasis: ["lose-weight", "build-strength"],
    ggsPregnant: "modify",
    ggsPostpartum: "modify",
  },
  {
    id: "ex_low_impact_intervals",
    name: "Low Impact Intervals",
    tier: "mobility",
    pattern: "cardio",
    equipment: "bodyweight",
    cue: "30s work, 30s rest. Step-jacks, mountain climbers, shadow boxing.",
    contraindications: [],
    goalEmphasis: ["lose-weight"],
    ggsPregnant: "safe",
    ggsPostpartum: "safe",
  },
];
